/**
 * TD Upsell Box Web Component
 * 
 * Manages the upsell tier selection, per-item dropdown color selectors,
 * free gift product selection, and updates the buy button text/price.
 * 
 */

if (!customElements.get('td-upsell-box')) {
  class TdUpsellBox extends HTMLElement {
    static EVENTS = {
      primaryShadeChange: 'td:primary-shade-change',
    };

    constructor() {
      super();

      /* ---- Initial state ---- */
      this.SHORT_NAME = this.dataset.shortName || 'item';
      this.CURRENCY_SUFFIX = this.dataset.currencySuffix || '';

      /* ---- DOM references (set in connectedCallback) ---- */
      this.tierElements = [];
      this.tierInputs = [];
      this.buyButtonTextEl = null;
      this.buyButtonPriceEl = null;
      this.stickyBuyButtonPriceEl = null;
      this.buyButtonSeparatorEl = null;
      this.submitButton = null;
      this.stickySubmitButton = null;
      this.stickyBuyButtonTextEl = null;
      this.stickyBuyButtonSeparatorEl = null;

      /* ---- Selection state ---- */
      this.selectedTier = null;
      this.slotSelections = {};
      this.freeGiftSelection = null;

      /* ---- Bound handlers for cleanup ---- */
      this._boundDocClick = this._onDocumentClick.bind(this);
      this._boundGuidanceClick = this._onGuidanceClick.bind(this);

      /* ---- Pub/sub unsubscriber for variant change ---- */
      this.variantChangeUnsubscriber = null;

      /* ---- MutationObserver for dropdown changes ---- */
      this.dropdownObserver = null;
    }

    connectedCallback() {
      const container = this.closest('.td-product__info-container--variation') || this.closest('.td-product__details');
      this.submitButton = container?.querySelector('.product-form__submit') || null;
      this.buyNowButton = container?.querySelector('[data-buy-now]') || null;
      this.tierElements = [...this.querySelectorAll('.td-upsell-box__tier')];
      this.tierInputs = [...this.querySelectorAll('.td-upsell-box__tier-input')];

      if (!this.tierElements.length) return;

      if (container) {
        this.buyButtonTextEl = container.querySelector('[data-buy-button-text]');
        this.buyButtonPriceEl = container.querySelector('[data-product-final-price]');
        this.buyButtonSeparatorEl = container.querySelector('[data-buy-button-separator]');
      }

      if (container) {
        this.stickySubmitButton = container.querySelector('[data-sticky-submit-button]');
        this.stickyBuyButtonTextEl = container.querySelector('[data-sticky-buy-button-text]');
        this.stickyBuyButtonSeparatorEl = container.querySelector('[data-sticky-buy-button-separator]');
        this.stickyBuyButtonPriceEl = container.querySelector('.td-product-form__sticky-bar__price');
      }

      if (this.stickySubmitButton && !this.stickyBuyButtonTextEl) {
        this.stickySubmitButton = null;
      }

      this._setupListeners();
      this._initDefaultState();
      this._setupDropdownChangeWatcher();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._boundDocClick);
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }

      if (this.submitButton) {
        this.submitButton.removeEventListener('click', this._boundGuidanceClick);
      }

      if (this.stickySubmitButton) {
        this.stickySubmitButton.removeEventListener('click', this._boundGuidanceClick);
      }

      if (this.buyNowButton) {
        this.buyNowButton.removeEventListener('click', this._boundGuidanceClick);
      }

      /* Disconnect MutationObserver */
      if (this.dropdownObserver) {
        this.dropdownObserver.disconnect();
        this.dropdownObserver = null;
      }
    }

    /* ==================================================================
       LISTENERS
       ================================================================== */

    _setupListeners() {
      /* Tier radio change */
      this.tierInputs.forEach((input) => {
        input.addEventListener('change', this._onTierChange.bind(this));
      });

      /* Tier row click (clicking the row also selects the radio) */
      this.tierElements.forEach((tier) => {
        tier.addEventListener('click', (e) => {
          /* Don't interfere with dropdown interactions */
          if (e.target.closest('[data-dropdown-trigger]') ||
              e.target.closest('[data-dropdown-menu]')) return;

          const input = tier.querySelector('.td-upsell-box__tier-input');
          if (input && !input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });

      /* Dropdown trigger clicks */
      this.querySelectorAll('[data-dropdown-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', this._onDropdownTriggerClick.bind(this));
      });

      /* Dropdown option clicks */
      this.querySelectorAll('[data-slot-swatch]').forEach((option) => {
        option.addEventListener('click', this._onDropdownOptionClick.bind(this));
      });

      /* Free gift dropdown option clicks */
      this.querySelectorAll('[data-free-gift-swatch]').forEach((option) => {
        option.addEventListener('click', this._onFreeGiftOptionClick.bind(this));
      });

      /* Close all dropdowns when clicking outside */
      document.addEventListener('click', this._boundDocClick);

      /* Subscribe to variant change from the main variant picker (pub/sub) */
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this._onMainVariantChange.bind(this));

      if (this.submitButton) {
        this.submitButton.addEventListener('click', this._boundGuidanceClick);
      }

      if (this.stickySubmitButton) {
        this.stickySubmitButton.addEventListener('click', this._boundGuidanceClick);
      }

      if (this.buyNowButton) {
        this.buyNowButton.addEventListener('click', this._boundGuidanceClick);
      }
    }

    /* ==================================================================
       INITIALIZATION
       ================================================================== */

    /**
     * Sets up a MutationObserver to watch for changes on dropdown triggers.
     * Calls buildCartPayload() when any dropdown trigger changes.
     */
    _setupDropdownChangeWatcher() {
      /* Get all dropdown triggers including free gift triggers */
      const dropdownTriggers = this.querySelectorAll('[data-dropdown-trigger]');
      const freeGiftTriggers = this.querySelectorAll('[data-free-gift-trigger]');
      const allTriggers = [...dropdownTriggers, ...freeGiftTriggers];
      
      if (allTriggers.length === 0) return;

      /* Update cart items when any dropdown trigger changes */
      this.dropdownObserver = new MutationObserver(() => {
        this.buildCartPayload();
      });

      /* Watch all dropdown triggers for class and content changes */
      allTriggers.forEach((trigger) => {
        this.dropdownObserver.observe(trigger, {
          attributes: true,
          attributeFilter: ['class'],
          childList: true,
          subtree: true,
          characterData: true
        });
      });
    }

    _initDefaultState() {
      const checkedInput = this.querySelector('.td-upsell-box__tier-input:checked');
      if (checkedInput) {
        this.selectedTier = checkedInput.closest('.td-upsell-box__tier');
        this._updateUI();
        this.buildCartPayload();
      }
    }

    /* ==================================================================
       DROPDOWN BEHAVIOR
       ================================================================== */

    /**
     * Opens/closes a dropdown menu when its trigger is clicked.
     * Only allows opening if the parent tier is selected.
     */
    _onDropdownTriggerClick(e) {
      e.preventDefault();
      e.stopPropagation();

      const trigger = e.currentTarget;
      const dropdown = trigger.closest('[data-slot-dropdown]');
      if (!dropdown) return;

      /* Check if the parent tier is selected */
      const tier = dropdown.closest('.td-upsell-box__tier');
      if (!tier || !tier.classList.contains('is-selected')) {
        return;
      }

      const menu = dropdown.querySelector('[data-dropdown-menu]');
      if (!menu) return;

      const isOpen = menu.classList.contains('is-open');

      /* Close all open dropdowns first */
      this._closeAllDropdowns();

      /* Toggle this one */
      if (!isOpen) {
        dropdown.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        menu.classList.add('is-open');
      }
    }

    /**
     * When a dropdown option is selected, update the trigger display
     * and store the selection.
     */
    _onDropdownOptionClick(e) {
      e.preventDefault();
      e.stopPropagation();

      const option = e.currentTarget;
      const dropdown = option.closest('[data-slot-dropdown]');
      const slotContainer = option.closest('.td-upsell-box__slot');
      if (!dropdown || !slotContainer) return;

      const slotIndex = slotContainer.dataset.slotIndex;
      const variantId = option.dataset.variantId;
      const variantTitle = option.dataset.variantTitle;
      const swatchUrl = option.dataset.swatchUrl || '';
      const isPrimary = option.dataset.isPrimary === 'true';

      /* Mark active option */
      dropdown.querySelectorAll('.td-upsell-box__dropdown-option').forEach((opt) => {
        opt.classList.remove('is-active');
      });
      option.classList.add('is-active');

      /* Update the trigger display */
      const trigger = dropdown.querySelector('[data-dropdown-trigger]');
      if (trigger) {
        this._updateTriggerDisplay(trigger, variantTitle, swatchUrl);
      }

      /* Close the dropdown */
      this._closeAllDropdowns();

      /* Store selection */
      this.slotSelections[slotIndex] = {
        variantId: variantId,
        variantTitle: variantTitle
      };

      /* If Pencil 1, sync with main variant picker */
      if (isPrimary) {
        this._syncMainVariantPicker(variantId);
      }

      /* Sync this slot across all tiers (works for any slot index) */
      this._updateAllSlotsByIndex(slotIndex, variantId, variantTitle, swatchUrl);

      this._updateUI();
    }

    /**
     * When a free gift dropdown option is selected.
     */
    _onFreeGiftOptionClick(e) {
      e.preventDefault();
      e.stopPropagation();

      const option = e.currentTarget;
      const dropdown = option.closest('[data-slot-dropdown]');
      if (!dropdown) return;

      const variantTitle = option.dataset.variantTitle;
      const swatchUrl = option.dataset.swatchUrl || '';

      /* Mark active */
      dropdown.querySelectorAll('.td-upsell-box__dropdown-option').forEach((opt) => {
        opt.classList.remove('is-active');
      });
      option.classList.add('is-active');

      /* Update trigger */
      const trigger = dropdown.querySelector('[data-dropdown-trigger]');
      if (trigger) {
        this._updateTriggerDisplay(trigger, variantTitle, swatchUrl);
      }

      this._closeAllDropdowns();

      this.freeGiftSelection = {
        variantId: option.dataset.variantId,
        variantTitle: variantTitle
      };
    }

    /**
     * Updates the display of a dropdown trigger to show the selected option.
     * Uses createElement/setAttribute to avoid XSS from data-variant-title or swatch URLs.
     */
    _updateTriggerDisplay(trigger, title, swatchUrl) {
      const valueEl = trigger.querySelector('[data-dropdown-value]');
      const swatchImgEl = trigger.querySelector('[data-dropdown-swatch-img]') || trigger.querySelector('.td-upsell-box__dropdown-swatch');

      if (valueEl) {
        valueEl.textContent = title;
      }

      /* Show swatch image if we have one – build via DOM to escape title/URL */
      if (swatchImgEl && swatchUrl) {
        const img = document.createElement('img');
        img.src = swatchUrl;
        img.alt = title;
        img.loading = 'lazy';
        img.width = 27;
        img.height = 27;
        img.onerror = function () { this.style.display = 'none'; };
        swatchImgEl.replaceChildren(img);
        swatchImgEl.style.display = '';
      }

      /* Remove placeholder class since a value is now selected */
      trigger.classList.remove('td-upsell-box__dropdown-trigger--placeholder');
    }

    /**
     * Closes all open dropdown menus inside this component.
     */
    _closeAllDropdowns() {
      this.querySelectorAll('.td-upsell-box__dropdown-menu.is-open').forEach((menu) => {
        menu.classList.remove('is-open');
        const dropdown = menu.closest('[data-slot-dropdown]');
        const trigger = dropdown?.querySelector('[data-dropdown-trigger]');
        dropdown?.classList.remove('is-open');
        trigger?.setAttribute('aria-expanded', 'false');
      });
    }

    /**
     * Close dropdowns when clicking outside the component.
     */
    _onDocumentClick(e) {
      if (!this.contains(e.target)) {
        this._closeAllDropdowns();
      }
    }

    _onGuidanceClick(e) {
      const button = e.currentTarget;
      if (!button || !button.classList.contains('is-guidance-state')) return;

      e.preventDefault();
      e.stopPropagation();

      this.scrollToBundleChooser();
    }

    /* ==================================================================
       EVENT HANDLERS
       ================================================================== */

    /**
     * When a tier radio button changes, update the visual state
     * and the buy button text/price.
     */
    _onTierChange(e) {
      const input = e.target;
      const tier = input.closest('.td-upsell-box__tier');

      /* Remove selected state from all tiers */
      this.tierElements.forEach((t) => t.classList.remove('is-selected'));

      /* Add selected state to the new tier */
      tier.classList.add('is-selected');
      this.selectedTier = tier;

      /* Clear free gift selection when switching to a tier without free gift */
      if (!this._selectedTierHasFreeGift()) {
        this.freeGiftSelection = null;
      } else {
        /* Restore freeGiftSelection from DOM when switching back to tier with free gift */
        this._restoreFreeGiftSelectionFromDOM();
      }

      /* Close any open dropdowns from previous tier */
      this._closeAllDropdowns();

      this._updateUI();
    }

    /**
     * Updates all dropdowns with the given slot index across all tiers.
     * Works for any slot index (primary, secondary, tertiary, etc.).
     * @param {string} slotIndex - The slot index to update (e.g., "0", "1", "2", "3")
     * @param {string} variantId - The variant ID to set
     * @param {string} variantTitle - The variant title
     * @param {string} swatchUrl - The swatch image URL
     */
    _updateAllSlotsByIndex(slotIndex, variantId, variantTitle, swatchUrl) {
      const variantIdStr = String(variantId);

      /* Update all dropdowns with this slot index across all tiers */
      this.querySelectorAll(`[data-slot-index="${slotIndex}"]`).forEach((slot) => {
        /* Mark the active option in the dropdown */
        slot.querySelectorAll('[data-slot-swatch]').forEach((opt) => {
          opt.classList.toggle('is-active', opt.dataset.variantId === variantIdStr);
        });

        /* Update trigger display */
        const trigger = slot.querySelector('[data-dropdown-trigger]');
        if (trigger) {
          this._updateTriggerDisplay(trigger, variantTitle, swatchUrl || '');
        }
      });

      /* Update slot selection for all slots with this index */
      this.slotSelections[slotIndex] = {
        variantId: variantIdStr,
        variantTitle: variantTitle
      };
    }

    /**
     * When the main variant picker changes, sync primary variant (slot 0) triggers across all tiers.
     * Receives pub/sub event: { data: { sectionId, html, variant } }
     * Swatch URL is read from the matching option (file-based swatches), not variant.featured_image.
     */
    _onMainVariantChange(event) {
      if (!event?.data?.variant) return;

      const section = this.closest('section');
      const ourSectionId = section?.id?.replace('shopify-section-', '') || section?.dataset?.section || '';
      if (ourSectionId && event.data.sectionId !== ourSectionId) return;

      const variant = event.data.variant;
      const variantId = String(variant.id);
      const variantTitle = variant.title;

      const matchingOption = this.querySelector(`[data-slot-index="0"] [data-slot-swatch][data-variant-id="${variantId}"]`);
      const swatchUrl = matchingOption?.dataset?.swatchUrl || variant.featured_image?.url || '';

      this._updateAllSlotsByIndex('0', variantId, variantTitle, swatchUrl);
      this._updateUI();
    }

    /* ==================================================================
       UI UPDATES
       ================================================================== */

    _updateUI() {
      if (!this.selectedTier) return;

      const input = this.selectedTier.querySelector('.td-upsell-box__tier-input');
      if (!input) return;

      const qty = parseInt(input.value, 10);
      const totalPrice = parseInt(input.dataset.tierTotal, 10);

      /* Update buy button text */
      this._updateBuyButtonText(qty);

      /* Update buy button price */
      this._updateBuyButtonPrice(totalPrice);

      /* Update cart items */
      this.buildCartPayload();
    }

    /**
     * Updates the CTA button text based on the selected tier quantity.
     */
    _updateBuyButtonText(qty) {
      if (!this.buyButtonTextEl) return;

      let text = '';
      if (qty === 1) {
        text = 'ADD TO CART';
      } else if (this._selectedTierHasFreeGift()) {
        text = 'GET THE BUNDLE + FREE GIFT';
      } else {
        text = 'GET THE BUNDLE';
      }

      this.setBuyButtonText(text);
    }

    /**
     * Gets the free gift product name from the selected tier.
     * @returns {string} Free gift product name or empty string
     */
    _getFreeGiftName() {
      const freeGiftSlot = this.selectedTier?.querySelector('[data-free-gift-slot]');
      return freeGiftSlot?.dataset.freeGiftName || '';
    }

    /**
     * Updates the buy button price display.
     * Appends currency suffix (e.g. " CAD") when Canadian shoppers.
     */
    _updateBuyButtonPrice(totalPrice) {
      const formattedPrice = this._formatMoney(totalPrice) + this.CURRENCY_SUFFIX;
      const priceElements = [this.buyButtonPriceEl, this.stickyBuyButtonPriceEl].filter(Boolean);
      if (!priceElements.length) return;

      priceElements.forEach((priceElement) => {
        priceElement.textContent = formattedPrice;
      });
    }

    /* ==================================================================
       SYNC WITH MAIN VARIANT PICKER
       ================================================================== */

    /**
     * Triggers a click on the main variant picker's matching radio input
     * to keep the product variant in sync.
     */
    _syncMainVariantPicker(variantId) {
      const mainPicker = this._getMainVariantPicker();
      if (!mainPicker) return;

      const variantsData = mainPicker.querySelector('.product-variants');
      const productOptionsData = mainPicker.querySelector('.product-options');
      if (!variantsData) return;

      try {
        const variants = JSON.parse(variantsData.textContent);
        const productOptions = productOptionsData ? JSON.parse(productOptionsData.textContent) : [];
        const targetVariant = variants.find((v) => String(v.id) === String(variantId));
        if (!targetVariant) return;

        const colorOption = productOptions.find((option) => option.name && option.name.toLowerCase().includes('color'));
        const colorOptionIndex = colorOption?.position ? colorOption.position - 1 : 0;
        const targetShadeValue = targetVariant.options?.[colorOptionIndex] ?? targetVariant.option1;
        const inputs = mainPicker.querySelectorAll('input[type="radio"]');
        const select = mainPicker.querySelector('select.select__select');
        let synced = false;

        for (const input of inputs) {
          const isMatchingInput = colorOption?.name
            ? input.name === colorOption.name && input.value === targetShadeValue
            : input.value === targetShadeValue;

          if (!isMatchingInput) continue;

          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          this.dispatchEvent(new CustomEvent(TdUpsellBox.EVENTS.primaryShadeChange, {
            bubbles: true,
            detail: {
              inputName: input.name,
              shadeValue: input.value,
              variantId: String(targetVariant.id),
            },
          }));
          synced = true;
          break;
        }

        if (!synced && select) {
          const matchingOption = Array.from(select.options).find((option) => option.value === targetShadeValue);
          if (matchingOption) {
            select.value = matchingOption.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            this.dispatchEvent(new CustomEvent(TdUpsellBox.EVENTS.primaryShadeChange, {
              bubbles: true,
              detail: {
                inputName: select.name,
                shadeValue: matchingOption.value,
                variantId: String(targetVariant.id),
              },
            }));
          }
        }
      } catch (err) {
        console.error('[td-upsell-box] Error syncing main variant picker:', err);
      }
    }

    /* ==================================================================
       CART DATA
       ================================================================== */

    /**
     * Builds the cart payload for the current upsell selection.
     * Returns items array for product form submission; also updates submit button state during selection.
     * @param {boolean} [skipButtonUpdate=false] - When true, do not call enable/disableSubmitButton.
     *   Use when called during form submit (product-form manages button state during submit).
     * @returns {Array|null} Items array for cart add, [] for single-item tier, or null when bundle tier is incomplete.
     */
    buildCartPayload(skipButtonUpdate = false) {
      if (!this.selectedTier) return [];

      const input = this.selectedTier.querySelector('.td-upsell-box__tier-input');
      if (!input) return [];

      const qty = parseInt(input.value, 10);
      let items = [];

      /* Single item */
      if (qty === 1) {
        if (!skipButtonUpdate) this.enableSubmitButton();
        return [];
      }

      /* Bundle tier: return null when incomplete so product-form can block submission */
      const productRoot = this.closest('td-shade-finder, quick-add-modal, section, body') || document;
      const primaryVariantInput = productRoot.querySelector('.product-variant-id');
      if (!primaryVariantInput) {
        if (!skipButtonUpdate) this.disableSubmitButton();
        return skipButtonUpdate ? null : [];
      }

      /* Validate all required slots are filled */
      for (let i = 1; i < qty; i++) {
        const slot = this.slotSelections[String(i)];
        if (!slot || !slot.variantId) {
          if (!skipButtonUpdate) this.disableSubmitButton();
          return skipButtonUpdate ? null : [];
        }
      }

      /* When tier has free gift: require selection (no placeholder on trigger) */
      if (this._selectedTierHasFreeGift() && !this._freeGiftIsSelected()) {
        if (!skipButtonUpdate) this.disableSubmitButton();
        return skipButtonUpdate ? null : [];
      }

      /* Build cart items - all validations passed */
      items.push({
        id: parseInt(primaryVariantInput.value, 10),
        quantity: 1
      });
      for (let i = 1; i < qty; i++) {
        items.push({
          id: parseInt(this.slotSelections[String(i)].variantId, 10),
          quantity: 1
        });
      }
      if (this._selectedTierHasFreeGift() && this.freeGiftSelection) {
        items.push({
          id: parseInt(this.freeGiftSelection.variantId, 10),
          quantity: 1,
          properties: { '_freePDPGift': 'true' }
        });
      }

      /* When tier has no free gift: clear selection and strip from payload */
      if (!this._selectedTierHasFreeGift()) {
        this.freeGiftSelection = null;
        items = items.filter((item) => item.properties?._freeGift !== 'true');
      }

      if (!skipButtonUpdate) this.enableSubmitButton();
      return items;
    }


    /* ==================================================================
       FREE GIFT PRODUCT
       ================================================================== */

    /**
     * Checks if the selected tier has a free gift (from metaobject).
     * Uses presence of [data-free-gift-slot] in the tier DOM.
     * @returns {boolean} True if selected tier has free gift
     */
    _selectedTierHasFreeGift() {
      return !!this.selectedTier?.querySelector('[data-free-gift-slot]');
    }

    /**
     * Restores freeGiftSelection from the DOM when switching back to a tier with free gift.
     * When switching away, we null freeGiftSelection but the trigger display persists.
     * This reads the active option from the dropdown to restore state.
     */
    _restoreFreeGiftSelectionFromDOM() {
      if (!this.selectedTier) return;

      const activeOption = this.selectedTier.querySelector('[data-free-gift-swatch].is-active');
      if (activeOption && activeOption.dataset.variantId) {
        this.freeGiftSelection = {
          variantId: activeOption.dataset.variantId,
          variantTitle: activeOption.dataset.variantTitle || ''
        };
      }
    }

    /**
     * Checks if the free gift trigger in the selected tier still has the placeholder class.
     * Placeholder = nothing selected yet; cannot checkout.
     * @returns {boolean} True if trigger has placeholder (selection required)
     */
    _freeGiftTriggerHasPlaceholder() {
      const trigger = this.selectedTier?.querySelector('[data-free-gift-trigger]');
      if (!trigger) return false;
      return trigger.classList.contains('td-upsell-box__dropdown-trigger--placeholder');
    }

    /**
     * Checks if free gift is selected when required.
     * @returns {boolean} True if tier has no free gift, or free gift is selected
     */
    _freeGiftIsSelected() {
      if (!this._selectedTierHasFreeGift()) return true;
      return !this._freeGiftTriggerHasPlaceholder();
    }

    /* ==================================================================
       HELPERS
       ================================================================== */

    /**
     * Formats a Shopify price (in cents) to a currency string.
     * @param {number} cents - Price in cents
     * @returns {string} Formatted price string
     */
    _formatMoney(cents) {
      if (typeof cents !== 'number' || isNaN(cents)) {
        return typeof Shopify?.formatMoney === 'function' ? Shopify.formatMoney(0) : '0';
      }

      return typeof Shopify?.formatMoney === 'function'
        ? Shopify.formatMoney(cents)
        : String(Math.round(cents / 100));
    }

    _getMainVariantPicker() {
      const productRoot = this.closest('.td-product-main');
      if (productRoot) {
        const rootedPicker = productRoot.querySelector('variant-radios, variant-selects');
        if (rootedPicker) return rootedPicker;
      }

      const container = this.closest('.td-product__info-container--variation') || this.closest('.td-product__details');
      if (container) {
        const localPicker = container.querySelector('variant-radios, variant-selects');
        if (localPicker) return localPicker;
      }

      return this.closest('td-shade-finder, section, body')?.querySelector('variant-radios, variant-selects') || null;
    }

    disableSubmitButton(message) {
      if (this.submitButton) {
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('is-guidance-state');
      }
      if (this.stickySubmitButton) {
        this.stickySubmitButton.setAttribute('aria-disabled', true);
        this.stickySubmitButton.classList.add('is-guidance-state');
      }
      if (this.buyNowButton) {
        this.buyNowButton.setAttribute('aria-disabled', true);
        this.buyNowButton.classList.add('is-guidance-state');
      }

      const buttonMessage = message || this.getIncompleteBundleButtonText();
      this.setBuyButtonText(buttonMessage);
    }

    enableSubmitButton() {
      if (this.submitButton) {
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButton.classList.remove('is-guidance-state');
      }
      if (this.stickySubmitButton) {
        this.stickySubmitButton.removeAttribute('aria-disabled');
        this.stickySubmitButton.classList.remove('is-guidance-state');
      }
      if (this.buyNowButton) {
        this.buyNowButton.removeAttribute('aria-disabled');
        this.buyNowButton.classList.remove('is-guidance-state');
      }

      const input = this.selectedTier?.querySelector('.td-upsell-box__tier-input');
      const qty = Number.parseInt(input?.value || '0', 10);
      if (qty > 0) {
        this._updateBuyButtonText(qty);
      } else {
        this.setBuyButtonText('ADD TO CART');
      }
    }

    getIncompleteBundleButtonText() {
      if (!this.selectedTier) {
        return 'PICK YOUR SHADES';
      }

      const input = this.selectedTier.querySelector('.td-upsell-box__tier-input');
      const qty = Number.parseInt(input?.value || '0', 10);
      let missingShadeCount = 0;

      for (let i = 1; i < qty; i++) {
        const slot = this.slotSelections[String(i)];
        if (!slot || !slot.variantId) {
          missingShadeCount += 1;
        }
      }

      const needsFreeGift = this._selectedTierHasFreeGift() && !this._freeGiftIsSelected();

      if (missingShadeCount > 0 && needsFreeGift) {
        return 'PICK SHADES + GIFT';
      }

      if (missingShadeCount > 1) {
        return 'PICK YOUR SHADES';
      }

      if (missingShadeCount === 1) {
        return 'PICK YOUR SHADE';
      }

      if (needsFreeGift) {
        return 'PICK YOUR GIFT';
      }

      return 'COMPLETE BUNDLE';
    }

    setBuyButtonText(text) {
      const textElements = [this.buyButtonTextEl, this.stickyBuyButtonTextEl].filter(Boolean);
      if (!textElements.length) return;

      textElements.forEach((textElement) => {
        if (textElement.textContent === text) return;
        textElement.classList.remove('is-transitioning');
        void textElement.offsetWidth;
        textElement.textContent = text;
        textElement.setAttribute('data-buy-button-text', text);
        textElement.classList.add('is-transitioning');
      });

      clearTimeout(this.buyButtonTextTimeout);
      this.buyButtonTextTimeout = window.setTimeout(() => {
        textElements.forEach((textElement) => textElement.classList.remove('is-transitioning'));
      }, 220);
    }

    scrollToBundleChooser() {
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  customElements.define('td-upsell-box', TdUpsellBox);
}
